import { type KeyboardEvent, useEffect, useRef, useState } from 'react'
import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui/icon'
import { useAnchoredPopover } from '@/shared/ui/popover'

export interface LinkItem {
  title: string
  url: string
}

interface InlineLinksProps {
  links: LinkItem[]
  /** Правка доступна (иначе — только чтение-чипы). */
  editable: boolean
  /** Сохранение всего набора (replace-семантика). */
  onSave: (links: LinkItem[]) => void
  saving?: boolean
  emptyLabel?: string
}

function hostOf(url: string): string {
  try {
    return new URL(url).host
  } catch {
    return ''
  }
}

// Приводит введённое к абсолютному URL: «example.com» → «https://example.com/».
// null — адрес не разбирается (бэк такой отвергнет).
function absoluteUrl(raw: string): string | null {
  const v = raw.trim()
  if (v === '') return null
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `https://${v}`
  try {
    const u = new URL(withScheme)
    return u.host ? u.toString() : null
  } catch {
    return null
  }
}

// Фавиконка с сайта (origin/favicon.ico); при недоступности — иконка ссылки.
function Favicon({ url }: { url: string }) {
  const [failed, setFailed] = useState(false)
  let origin = ''
  try {
    origin = new URL(url).origin
  } catch {
    origin = ''
  }
  if (failed || !origin) return <Icon name="external-link" size={13} className="shrink-0" />
  return (
    <img
      src={`${origin}/favicon.ico`}
      alt=""
      width={14}
      height={14}
      className="shrink-0 rounded-[3px]"
      onError={() => setFailed(true)}
    />
  )
}

const chip =
  'inline-flex items-center gap-1.5 rounded-pill bg-bg-elevated px-2.5 py-1 text-xs text-text-secondary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent'

// Блок ссылок «тихим инлайном»: чтение — компактные чипы с фавиконкой; правка —
// те же чипы с крестиком удаления на hover + кнопка «+» с popover-формой
// (title/url). Сохранение всего набора (replace-семантика).
export function InlineLinks({
  links,
  editable,
  onSave,
  saving,
  emptyLabel = 'Ссылок нет',
}: InlineLinksProps) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')
  const [error, setError] = useState('')
  const addRef = useRef<HTMLDivElement>(null)
  const { anchorRef, popoverRef, popoverProps } = useAnchoredPopover<
    HTMLButtonElement,
    HTMLDivElement
  >({ open })

  // popover="manual" — light-dismiss выключен; закрываем по клику вне блока сами.
  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (!addRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  function reset() {
    setTitle('')
    setUrl('')
    setError('')
    setOpen(false)
  }
  function add() {
    const t = title.trim()
    const u = absoluteUrl(url)
    // Контракт бэка: непустое название + абсолютный URL со схемой и хостом.
    if (t === '') {
      setError('Укажите название')
      return
    }
    if (u === null) {
      setError('Адрес вида https://example.com')
      return
    }
    onSave([...links, { title: t, url: u }])
    reset()
  }
  function onFormKey(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault()
      add()
    }
    if (e.key === 'Escape') reset()
  }

  if (!editable) {
    if (links.length === 0) return <p className="text-sm text-text-tertiary">{emptyLabel}</p>
    return (
      <div className="flex flex-wrap gap-2">
        {links.map((link) => (
          <a
            key={`${link.title}-${link.url}`}
            href={link.url}
            target="_blank"
            rel="noreferrer"
            title={link.url}
            className={chip}
          >
            <Favicon url={link.url} />
            {link.title || hostOf(link.url) || link.url}
          </a>
        ))}
      </div>
    )
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-2', saving && 'opacity-60')}>
      {links.map((link, i) => (
        <span key={`${link.title}-${link.url}`} className="group relative inline-flex">
          <a
            href={link.url}
            target="_blank"
            rel="noreferrer"
            title={link.url}
            className={cn(chip, 'pr-7')}
          >
            <Favicon url={link.url} />
            {link.title || hostOf(link.url) || link.url}
          </a>
          <button
            type="button"
            aria-label={`Удалить ссылку ${link.title || link.url}`}
            onClick={() => onSave(links.filter((_, j) => j !== i))}
            className="-translate-y-1/2 absolute top-1/2 right-1 rounded-full p-0.5 text-text-tertiary opacity-0 transition-opacity hover:text-accent focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent group-hover:opacity-100"
          >
            <Icon name="close" size={12} />
          </button>
        </span>
      ))}

      <div ref={addRef} className="relative inline-flex">
        <button
          ref={anchorRef}
          type="button"
          aria-label="Добавить ссылку"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 rounded-pill border border-border-default border-dashed px-2 py-1 text-text-tertiary text-xs transition-colors hover:border-border-strong hover:text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
        >
          <Icon name="plus" size={13} />
        </button>
        {open && (
          <div
            ref={popoverRef}
            {...popoverProps}
            className="m-0 flex w-72 flex-col gap-2 rounded-card border border-border-default bg-bg-surface p-3 shadow-black/40 shadow-lg"
          >
            <input
              // Форма появляется по клику — фокус на первом поле ожидаем.
              ref={(el) => el?.focus()}
              aria-label="Название ссылки"
              value={title}
              placeholder="Название"
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={onFormKey}
              className="rounded-control border border-border-default bg-bg-sunken px-2.5 py-1.5 text-sm text-text-primary outline-none placeholder:text-text-tertiary focus:border-border-accent"
            />
            <input
              aria-label="URL ссылки"
              value={url}
              placeholder="https://…"
              inputMode="url"
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={onFormKey}
              className="rounded-control border border-border-default bg-bg-sunken px-2.5 py-1.5 text-sm text-text-primary outline-none placeholder:text-text-tertiary focus:border-border-accent"
            />
            {error && <p className="text-accent text-xs">{error}</p>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={reset}
                className="rounded-control px-2.5 py-1 text-text-tertiary text-xs transition-colors hover:text-text-secondary"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={add}
                disabled={url.trim() === '' || title.trim() === ''}
                className="rounded-control bg-bg-raised px-2.5 py-1 text-text-primary text-xs transition-colors hover:bg-bg-elevated disabled:opacity-40"
              >
                Добавить
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
